/*
 * <yarnly-facets>
 *
 * Progressive enhancement for the collection and search filter forms rendered by
 * snippets/facets.liquid and snippets/facet-active.liquid. Without JavaScript the
 * forms and chips are plain GET links; with JavaScript this element rewrites the
 * URL, fetches the section through the Section Rendering API and swaps the parts
 * that changed. Every listener is delegated so the element keeps working after it
 * has replaced its own markup.
 */
(function () {
  'use strict';

  const S = window.Yarnly;
  if (!S) return;

  const SWAP_TARGETS = [
    '[data-facets-results]',
    '[data-facets-active]',
    '[data-facets-count]',
    '[data-facets-pagination]'
  ];

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/([^\w-])/g, '\\$1');
  }

  if (!customElements.get('yarnly-facets')) {
    class YarnlyFacets extends HTMLElement {
      constructor() {
        super();
        this.onChange = this.onChange.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onPopState = this.onPopState.bind(this);
        this.controller = null;
        this.pendingForm = null;
        this.applyPrice = S.utils.debounce(() => {
          this.apply({ form: this.pendingForm });
        }, 500);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId || '';
        this.baseUrl = this.dataset.baseUrl || window.location.pathname;
        this.addEventListener('change', this.onChange);
        this.addEventListener('input', this.onInput);
        this.addEventListener('click', this.onClick);
        this.addEventListener('submit', this.onSubmit);
        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('input', this.onInput);
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('submit', this.onSubmit);
        window.removeEventListener('popstate', this.onPopState);
        if (this.controller) {
          this.controller.abort();
          this.controller = null;
        }
      }

      /* ---- events ------------------------------------------------------- */

      onChange(event) {
        const target = event.target;
        if (!target || typeof target.matches !== 'function') return;

        if (target.matches('[data-facets-sort]')) {
          this.syncSort(target.value);
          this.apply({ form: this.formFor(null) });
          return;
        }

        const form = target.closest('[data-facets-form]');
        if (!form) return;
        // Price fields re-run through the debounced input handler instead.
        if (target.matches('[data-facets-price]')) return;
        if (target.name === 'sort_by') this.syncSort(target.value);
        this.apply({ form: form });
      }

      onInput(event) {
        const target = event.target;
        if (!target || typeof target.matches !== 'function') return;
        if (!target.matches('[data-facets-price]')) return;
        const form = target.closest('[data-facets-form]');
        if (!form) return;
        this.pendingForm = form;
        this.applyPrice();
      }

      onClick(event) {
        if (event.defaultPrevented) return;

        const applyButton = event.target.closest('[data-facets-apply]');
        if (applyButton && this.contains(applyButton)) {
          event.preventDefault();
          const formId = applyButton.getAttribute('form');
          let form = formId ? this.querySelector('#' + cssEscape(formId)) : null;
          if (!form) {
            const drawer = applyButton.closest('yarnly-drawer');
            form = drawer ? drawer.querySelector('[data-facets-form]') : null;
          }
          if (form) this.apply({ form: form });
          this.closeDrawer(applyButton);
          return;
        }

        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (typeof event.button === 'number' && event.button !== 0) return;

        const link = event.target.closest(
          '.facets__chip, [data-facets-clear], [data-facets-pagination] a[href]'
        );
        if (!link || !this.contains(link) || !link.getAttribute('href')) return;

        event.preventDefault();
        const paginating = Boolean(link.closest('[data-facets-pagination]'));
        this.apply({ url: link.href, scroll: paginating });
      }

      onSubmit(event) {
        const form = event.target.closest('[data-facets-form]');
        if (!form || !this.contains(form)) return;
        event.preventDefault();
        this.apply({ form: form });
      }

      onPopState() {
        this.apply({ url: window.location.href, push: false });
      }

      /* ---- state -------------------------------------------------------- */

      formFor(element) {
        const owned = element && element.closest ? element.closest('[data-facets-form]') : null;
        return owned || this.querySelector('[data-facets-form]');
      }

      // Both facet forms and the toolbar select carry the same sort value.
      syncSort(value) {
        this.querySelectorAll('[name="sort_by"]').forEach((control) => {
          control.value = value;
        });
      }

      currentSort(form) {
        const inForm = form ? form.querySelector('[name="sort_by"]') : null;
        if (inForm && inForm.value) return inForm.value;
        const toolbar = this.querySelector('[data-facets-sort]');
        return toolbar ? toolbar.value : '';
      }

      collect(form) {
        const params = new URLSearchParams();
        if (form) {
          const fields = form.querySelectorAll('input[name], select[name]');
          for (let i = 0; i < fields.length; i += 1) {
            const field = fields[i];
            const name = field.name;
            if (!name || field.disabled) continue;
            if (name === 'sort_by') continue;
            if (field.type === 'checkbox' || field.type === 'radio') {
              if (field.checked) params.append(name, field.value);
              continue;
            }
            const value = String(field.value == null ? '' : field.value).trim();
            if (!value) continue;
            params.append(name, value);
          }
        }
        const sort = this.currentSort(form);
        if (sort) params.set('sort_by', sort);
        return params;
      }

      /* ---- fetch and swap ------------------------------------------------ */

      apply(options) {
        const opts = options || {};
        let url = opts.url;
        if (!url) {
          const params = this.collect(opts.form);
          const parts = this.baseUrl.split('?');
          // Context the base URL carries (the search term, for instance) is kept
          // whenever the form did not supply it itself.
          if (parts[1]) {
            new URLSearchParams(parts[1]).forEach((value, key) => {
              if (!params.has(key)) params.set(key, value);
            });
          }
          const query = params.toString();
          url = query ? parts[0] + '?' + query : parts[0];
        }
        this.render(url.split('#')[0], opts);
      }

      render(url, options) {
        const opts = options || {};

        if (this.controller) this.controller.abort();
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        this.controller = controller;

        this.setBusy(true);

        if (opts.push !== false && window.history && typeof window.history.pushState === 'function') {
          window.history.pushState({ yarnlyFacets: this.sectionId }, '', url);
        }

        const fetchUrl =
          url + (url.includes('?') ? '&' : '?') + 'section_id=' + encodeURIComponent(this.sectionId);

        fetch(fetchUrl, controller ? { signal: controller.signal } : {})
          .then((response) => {
            if (!response.ok) throw new Error('Facets request failed');
            return response.text();
          })
          .then((text) => {
            if (controller && this.controller !== controller) return;
            this.controller = null;
            this.update(text, opts);
          })
          .catch((error) => {
            if (error && error.name === 'AbortError') return;
            if (this.controller === controller) this.controller = null;
            this.setBusy(false);
          });
      }

      update(html, options) {
        const opts = options || {};
        const doc = S.utils.parseHTML(html);
        const source = doc.querySelector('yarnly-facets') || doc.body;
        if (!source) {
          this.setBusy(false);
          return;
        }

        const active = document.activeElement;
        const focusId = active && active.id && this.contains(active) ? active.id : null;
        const openGroups = this.openGroupIds();

        SWAP_TARGETS.forEach((selector) => {
          const target = this.querySelector(selector);
          if (!target) return;
          const incoming = source.querySelector(selector);
          target.innerHTML = incoming ? incoming.innerHTML : '';
        });

        this.querySelectorAll('[data-facets-form]').forEach((form) => {
          let incoming = form.id ? source.querySelector('#' + cssEscape(form.id)) : null;
          if (!incoming) incoming = source.querySelector('[data-facets-form]');
          if (incoming) form.innerHTML = incoming.innerHTML;
        });

        const badge = this.querySelector('[data-facets-badge]');
        const incomingBadge = source.querySelector('[data-facets-badge]');
        if (badge && incomingBadge) {
          badge.textContent = incomingBadge.textContent;
          badge.hidden = incomingBadge.hasAttribute('hidden');
        }

        const sortSelect = this.querySelector('[data-facets-sort]');
        const incomingSort = source.querySelector('[data-facets-sort]');
        if (sortSelect && incomingSort) sortSelect.value = incomingSort.value;

        this.restoreGroups(openGroups);
        this.setBusy(false);

        if (focusId) {
          const restored = this.querySelector('#' + cssEscape(focusId));
          if (restored && typeof restored.focus === 'function') restored.focus({ preventScroll: true });
        }

        if (opts.scroll) this.scrollToResults();

        const count = this.querySelector('[data-facets-count]');
        if (count) S.utils.announce(count.textContent.trim());
      }

      /* ---- helpers ------------------------------------------------------- */

      setBusy(isBusy) {
        const results = this.querySelector('[data-facets-results]');
        if (results) results.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }

      openGroupIds() {
        const ids = [];
        this.querySelectorAll('details[id]').forEach((group) => {
          if (group.open) ids.push(group.id);
        });
        return ids;
      }

      restoreGroups(ids) {
        if (!ids || !ids.length) return;
        ids.forEach((id) => {
          const group = this.querySelector('#' + cssEscape(id));
          if (group && group.tagName === 'DETAILS') group.open = true;
        });
      }

      closeDrawer(element) {
        let drawer = element && element.closest ? element.closest('yarnly-drawer') : null;
        if (!drawer) drawer = this.querySelector('yarnly-drawer.is-open');
        if (drawer && typeof drawer.close === 'function') drawer.close();
      }

      scrollToResults() {
        const results = this.querySelector('[data-facets-results]');
        if (!results || typeof results.scrollIntoView !== 'function') return;
        const reduced = typeof S.utils.isReducedMotion === 'function' && S.utils.isReducedMotion();
        results.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      }
    }

    customElements.define('yarnly-facets', YarnlyFacets);
  }
})();
